import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/i18n/LanguageContext";
import { getConsent, saveConsent, initClarity, initGA } from "@/consent";
import { SITE } from "@/config";

export default function CookieConsent() {
  const { lang, t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c) setShowBanner(true);
    else if (c.analytics) { initClarity(SITE.clarityId); initGA(SITE.gaId); }
  }, []);

  useEffect(() => {
    const open = () => {
      const c = getConsent();
      setAnalytics(Boolean(c && c.analytics));
      setShowBanner(false);
      setShowSettings(true);
    };
    window.addEventListener("open-cookie-settings", open);
    return () => window.removeEventListener("open-cookie-settings", open);
  }, []);

  const apply = (analyticsGranted) => {
    saveConsent({ necessary: true, analytics: analyticsGranted, advertising: false });
    if (analyticsGranted) { initClarity(SITE.clarityId); initGA(SITE.gaId); }
    setShowBanner(false);
    setShowSettings(false);
  };

  const openSettings = () => {
    const c = getConsent();
    setAnalytics(Boolean(c && c.analytics));
    setShowSettings(true);
  };

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-4 inset-x-4 sm:bottom-6 z-[90] flex justify-center pointer-events-none" data-testid="cookie-banner">
          <div className="pointer-events-auto w-full max-w-2xl bg-white rounded-2xl border border-line shadow-xl p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-xl bg-sand flex items-center justify-center shrink-0 mt-0.5">
                <Cookie className="w-4 h-4 text-terra" />
              </span>
              <div>
                <p className="font-serif text-lg font-semibold">{t.cookies.banner_title}</p>
                <p className="text-sm text-muted2 mt-1 leading-relaxed">{t.cookies.banner_text}</p>
                <Link to={`/${lang}/privacy`} className="inline-block text-xs text-terra underline underline-offset-2 mt-2 hover:text-ink transition-colors" data-testid="cookie-policy-link">
                  {t.privacy.link}
                </Link>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button onClick={() => apply(true)} data-testid="cookie-accept-button" className="rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-ink">
                {t.cookies.accept}
              </button>
              <button onClick={() => apply(false)} data-testid="cookie-reject-button" className="rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-sage hover:text-sage">
                {t.cookies.reject}
              </button>
              <button onClick={openSettings} data-testid="cookie-settings-button" className="rounded-full px-5 py-2.5 text-sm font-medium text-muted2 transition-colors hover:text-terra">
                {t.cookies.settings}
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-cream sm:max-w-md" data-testid="cookie-settings-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t.cookies.settings_title}</DialogTitle>
            <DialogDescription>{t.cookies.banner_text}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-white px-4 py-3">
              <div>
                <p className="text-sm font-semibold">{t.cookies.necessary}</p>
                <p className="text-xs text-muted2 mt-0.5">{t.cookies.necessary_desc}</p>
              </div>
              <Switch checked disabled data-testid="cookie-necessary-switch" />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-white px-4 py-3">
              <div>
                <p className="text-sm font-semibold">{t.cookies.analytics}</p>
                <p className="text-xs text-muted2 mt-0.5">{t.cookies.analytics_desc}</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-analytics-switch" />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-white px-4 py-3 opacity-60">
              <div>
                <p className="text-sm font-semibold">{t.cookies.advertising}</p>
                <p className="text-xs text-muted2 mt-0.5">{t.cookies.advertising_desc}</p>
              </div>
              <Switch checked={false} disabled data-testid="cookie-advertising-switch" />
            </div>
            <button onClick={() => apply(analytics)} data-testid="cookie-save-settings-button" className="w-full rounded-full bg-sage py-3 text-sm font-semibold text-white transition-colors hover:bg-ink">
              {t.cookies.save}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
